import { Button } from '../ui/Button'
import { isGenerationOutbound } from './conversationHelpers'
import type { PendingDisclosure } from './types'
import './defendsphere-ai.css'

type TranslateFn = (key: string, fallback?: string) => string

export type DefendSphereAiOutboundPreviewProps = {
  pending: PendingDisclosure
  t: TranslateFn
  busy: boolean
  editUserPrompt: string
  editContextSummary: string
  editIncludePlatform: boolean
  editIncludeDocs: boolean
  editIncludeContext: boolean
  showTransmitted: boolean
  onEditUserPrompt: (v: string) => void
  onEditContextSummary: (v: string) => void
  onEditIncludePlatform: (v: boolean) => void
  onEditIncludeDocs: (v: boolean) => void
  onEditIncludeContext: (v: boolean) => void
  onShowTransmitted: (v: boolean) => void
  onSaveEdits: () => void
  onApprove: (conversationScope: boolean) => void
  onDecline: () => void
  onDismiss: () => void
}

export function DefendSphereAiOutboundPreview({
  pending,
  t,
  busy,
  editUserPrompt,
  editContextSummary,
  editIncludePlatform,
  editIncludeDocs,
  editIncludeContext,
  showTransmitted,
  onEditUserPrompt,
  onEditContextSummary,
  onEditIncludePlatform,
  onEditIncludeDocs,
  onEditIncludeContext,
  onShowTransmitted,
  onSaveEdits,
  onApprove,
  onDecline,
  onDismiss,
}: DefendSphereAiOutboundPreviewProps) {
  const generation = isGenerationOutbound(pending)
  const idBase = `ds-ai-outbound-${pending.requestId || 'pending'}`

  return (
    <div className="ds-ai-outbound" role="dialog" aria-labelledby={`${idBase}-title`}>
      <h3 id={`${idBase}-title`} className="ds-ai-outbound__title">
        {t('complianceCenterUi.aiCiso.outboundPreviewTitle', 'Outbound request preview')}
      </h3>
      <p className="ds-ai-outbound__meta">
        <strong>{t('complianceCenterUi.aiCiso.disclosureProvider', 'Provider')}:</strong>{' '}
        {pending.providerLabel || pending.providerKey || 'External AI'}
        {pending.modelKey ? ` · ${pending.modelKey}` : ''}
      </p>
      <p className="ds-ai-outbound__meta">
        <strong>{t('complianceCenterUi.aiCiso.disclosurePurpose', 'Purpose')}:</strong>{' '}
        {pending.purposeLabel || pending.purpose || 'External AI'}
      </p>
      <p className="ds-ai-outbound__meta">
        <strong>{t('complianceCenterUi.aiCiso.disclosureMode', 'Disclosure mode')}:</strong>{' '}
        {pending.productDisclosureMode || 'PREVIEW_BEFORE_SEND'}
      </p>
      {pending.payloadHash ? (
        <p className="ds-ai-outbound__hash">
          <strong>{t('complianceCenterUi.aiCiso.payloadHash', 'Payload hash')}:</strong> {pending.payloadHash}
          {pending.payloadRevision != null ? ` · rev ${pending.payloadRevision}` : ''}
        </p>
      ) : null}
      {pending.expiresAt ? (
        <p className="ds-ai-outbound__meta">
          <strong>{t('complianceCenterUi.aiCiso.expiresAt', 'Expires')}:</strong>{' '}
          {new Date(pending.expiresAt).toLocaleString()}
        </p>
      ) : null}
      {(pending.warnings || []).length ? (
        <ul className="ds-ai-outbound__warnings">
          {pending.warnings!.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      ) : null}

      {generation ? (
        <div className="ds-ai-outbound__edits">
          <label className="ds-ai-label" htmlFor={`${idBase}-prompt`}>
            {t('complianceCenterUi.aiCiso.editUserPrompt', 'User prompt (editable)')}
          </label>
          <textarea
            id={`${idBase}-prompt`}
            className="ds-ai-input"
            value={editUserPrompt}
            onChange={(e) => onEditUserPrompt(e.target.value)}
            disabled={busy}
          />
          <label className="ds-ai-label" htmlFor={`${idBase}-context`}>
            {t('complianceCenterUi.aiCiso.editContextSummary', 'Context summary (editable)')}
          </label>
          <textarea
            id={`${idBase}-context`}
            className="ds-ai-input"
            value={editContextSummary}
            onChange={(e) => onEditContextSummary(e.target.value)}
            disabled={busy}
          />
          <div className="ds-ai-outbound__checks">
            <label className="ds-ai-check">
              <input
                type="checkbox"
                checked={editIncludePlatform}
                onChange={(e) => onEditIncludePlatform(e.target.checked)}
                disabled={busy}
              />
              {t('complianceCenterUi.aiCiso.includePlatformData', 'Include platform data')}
            </label>
            <label className="ds-ai-check">
              <input
                type="checkbox"
                checked={editIncludeDocs}
                onChange={(e) => onEditIncludeDocs(e.target.checked)}
                disabled={busy}
              />
              {t('complianceCenterUi.aiCiso.includeDocumentRag', 'Include document RAG')}
            </label>
            <label className="ds-ai-check">
              <input
                type="checkbox"
                checked={editIncludeContext}
                onChange={(e) => onEditIncludeContext(e.target.checked)}
                disabled={busy}
              />
              {t('complianceCenterUi.aiCiso.includeContext', 'Include context summary')}
            </label>
          </div>
          <Button type="button" size="small" color="outline" disabled={busy} onClick={() => onSaveEdits()}>
            {t('complianceCenterUi.aiCiso.revalidateEdits', 'Revalidate edits')}
          </Button>
          <details
            className="ds-ai-outbound__transmitted"
            open={showTransmitted}
            onToggle={(e) => onShowTransmitted((e.target as HTMLDetailsElement).open)}
          >
            <summary>
              {t('complianceCenterUi.aiCiso.transmittedPayload', 'Exact provider-visible payload')}
            </summary>
            <pre className="ds-ai-pre">{JSON.stringify(pending.transmitted?.messages || [], null, 2)}</pre>
            {pending.transmitted?.systemInstructionDisclosure ? (
              <p className="ds-ai-muted">{pending.transmitted.systemInstructionDisclosure}</p>
            ) : null}
          </details>
        </div>
      ) : (
        <div className="ds-ai-outbound__section">
          <p className="ds-ai-label">{t('complianceCenterUi.aiCiso.disclosureDataSent', 'Data to be sent')}:</p>
          <ul>
            {(
              pending.dataSent || [
                'user question',
                'sanitized summary',
                'recommendation titles',
                'standards references',
              ]
            ).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="ds-ai-outbound__section">
        <p className="ds-ai-label">{t('complianceCenterUi.aiCiso.disclosureDataNotSent', 'Data NOT sent')}:</p>
        <ul>
          {(
            pending.excluded?.dataNotSent ||
            pending.dataNotSent ||
            pending.preview?.dataNotSent || [
              'credentials',
              'raw hostnames and IPs',
              'personal identifiers',
              'provider API keys',
            ]
          ).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        {(pending.redactedCategories || []).length ? (
          <p className="ds-ai-muted">
            {t('complianceCenterUi.aiCiso.redactedCategories', 'Redacted')}:{' '}
            {pending.redactedCategories!.join(', ')}
          </p>
        ) : null}
      </div>

      <div className="ds-ai-outbound__actions">
        <Button type="button" size="small" color="primary" disabled={busy} onClick={() => onApprove(false)}>
          {t('complianceCenterUi.aiCiso.disclosureApproveOnce', 'Approve & send')}
        </Button>
        {!generation ? (
          <Button type="button" size="small" color="outline" disabled={busy} onClick={() => onApprove(true)}>
            {t('complianceCenterUi.aiCiso.disclosureApproveConversation', 'Approve For Conversation')}
          </Button>
        ) : null}
        <Button type="button" size="small" color="outline" disabled={busy} onClick={() => onDecline()}>
          {t('complianceCenterUi.aiCiso.disclosureUseDeterministic', 'Use Deterministic Answer')}
        </Button>
        <Button type="button" size="small" color="outline" disabled={busy} onClick={() => onDismiss()}>
          {t('common.cancel', 'Cancel')}
        </Button>
      </div>
    </div>
  )
}
